import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Drawer, PassCard } from '@/components/campus';
import { RefreshCw, XCircle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date helper
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const PassDetailDrawer = ({
  pass,
  guest,
  event,
  isOpen,
  onClose,
  onRevoke,
  onRegenerate,
  session,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  
  if (!pass || !guest) return null;
  
  const handleRevoke = async () => {
    if (!window.confirm('Are you sure you want to revoke this pass? The guest will no longer be able to check in.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/passes/${pass.id}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      
      if (res.ok) {
        toast.success('Pass revoked');
        onRevoke?.();
        onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to revoke pass');
      }
    } catch (error) {
      toast.error('Failed to revoke pass');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleRegenerate = async () => {
    if (!window.confirm('This will revoke the current pass and generate a new one. Continue?')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      // First revoke the old pass
      await fetch(`${API_URL}/api/passes/${pass.id}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      
      // Generate new pass
      const res = await fetch(`${API_URL}/api/passes/generate?guest_id=${guest.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      
      if (res.ok) {
        toast.success('New pass generated');
        onRegenerate?.();
        onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to regenerate pass');
      }
    } catch (error) {
      toast.error('Failed to regenerate pass');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleDownload = () => {
    // Trigger download from PassCard QR
    const qrContainer = document.querySelector('[data-testid="pass-card"] .bg-white');
    if (!qrContainer) return;
    
    const svg = qrContainer.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const padding = 40;
      canvas.width = 300 + padding * 2;
      canvas.height = 400 + padding * 2;
      
      // Background
      ctx.fillStyle = '#0A0A0B';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // White QR background
      ctx.fillStyle = 'white';
      ctx.fillRect(padding, padding, 300, 300);
      
      // QR Code
      ctx.drawImage(img, padding + 50, padding + 50, 200, 200);
      
      // Text
      ctx.fillStyle = 'white';
      ctx.font = 'bold 18px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(guest.guest_name, canvas.width / 2, 380);
      
      ctx.font = '14px Inter, sans-serif';
      ctx.fillStyle = '#8B8B8B';
      ctx.fillText(event?.event_name || 'Event', canvas.width / 2, 405);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `pass-${guest.guest_name.replace(/\s+/g, '-')}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Guest Pass" width="lg">
      <div className="space-y-6">
        {/* Pass Card */}
        <PassCard
          guestName={guest.guest_name}
          eventName={event?.event_name || 'Unknown Event'}
          eventDate={event?.start_time ? formatDateTime(event.start_time) : undefined}
          eventLocation={event?.location}
          qrCodeData={pass.qr_code_data}
          status={pass.status}
          checkedInAt={pass.checked_in_at ? formatDateTime(pass.checked_in_at) : undefined}
          createdAt={formatDateTime(pass.created_at)}
          size="lg"
        />
        
        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={handleDownload}
            className="w-full bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20 hover:bg-electric-ice-blue/20"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Pass
          </Button>
          
          {pass.status === 'valid' && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleRegenerate}
                variant="outline"
                className="border-soft-amber/30 text-soft-amber hover:bg-soft-amber/10"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Regenerate
              </Button>
              <Button
                onClick={handleRevoke}
                variant="outline"
                className="border-signal-red/30 text-signal-red hover:bg-signal-red/10"
                disabled={isProcessing}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Revoke
              </Button>
            </div>
          )}
          
          {pass.status === 'used' && (
            <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20 text-center">
              <p className="text-cool-mint text-sm">
                This pass was used for check-in on {formatDateTime(pass.checked_in_at)}
              </p>
            </div>
          )}
          
          {pass.status === 'revoked' && (
            <div className="p-4 bg-signal-red/10 rounded-lg border border-signal-red/20 text-center">
              <p className="text-signal-red text-sm">
                This pass has been revoked and can no longer be used
              </p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default PassDetailDrawer;
